import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { connector } from '../services/api';
import { capitalizeError } from '../utils/errorUtils';
import './FineTuningGraphPage.css';
import './AdminPage.css';
import './admin/AdminShell.css';
import './connector/ConnectorShell.css';
import AdminTabBar from './admin/AdminTabBar';
import ConnectorSectionShell from './connector/ConnectorSectionShell';
import { resolveAdminTab, type AdminTabType } from './admin/adminTabConfig';
import { useAuthContext } from '../contexts/AuthContext';
import {
  resolveStoredIntegrationId,
  setStoredSelectedIntegrationId,
} from '../utils/selectedIntegrationStorage';

interface LineageNode {
  id: string;
  label?: string;
  type?: string;
  is_head?: boolean;
  [key: string]: any;
}

interface LineageEdge {
  id?: string;
  from_id: string;
  to_id: string;
  label?: string;
  [key: string]: any;
}

interface LineageData {
  nodes?: LineageNode[];
  edges?: LineageEdge[];
  meta?: {
    nodes_count?: number;
    edges_count?: number;
  };
  [key: string]: any;
}

const FineTuningGraphPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const resolvedTab = resolveAdminTab(location.pathname, location.search);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const graphIntegrationRestoreRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState<string | null>(null);
  
  const selectedIntegrationId = searchParams.get('integration_id') || '';
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Load integrations list
  useEffect(() => {
    const loadIntegrations = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await connector.listIntegrationsAdmin();
        const items = resp?.data ?? resp?.items ?? resp ?? [];
        setIntegrations(Array.isArray(items) ? items : []);
      } catch (e: any) {
        setError(capitalizeError(e?.message) || 'Failed to load integrations');
        setIntegrations([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadIntegrations();
  }, []);

  useEffect(() => {
    if (loading || integrations.length === 0) return;
    if (graphIntegrationRestoreRef.current) return;
    if (searchParams.get('integration_id')) {
      graphIntegrationRestoreRef.current = true;
      return;
    }

    graphIntegrationRestoreRef.current = true;
    const storedId = resolveStoredIntegrationId('admin', user?.id, integrations);
    if (!storedId) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set('integration_id', storedId);
    setSearchParams(newParams, { replace: true });
  }, [loading, integrations, searchParams, setSearchParams, user?.id]);

  // Load graph data
  useEffect(() => {
    if (!selectedIntegrationId) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const loadGraph = async () => {
      setGraphLoading(true);
      setGraphError(null);
      try {
        const response = await connector.getModelLineage({
          integration_id: selectedIntegrationId,
          view: 'ui',
          compact: true,
          latest_per_alias: true,
          limit_nodes: 120,
          limit_edges: 200,
        });

        if (response.success && response.data) {
          const graphData: LineageData = response.data;
          
          // Transform API nodes to ReactFlow nodes
          const apiNodes = graphData.nodes || [];
          const apiEdges = graphData.edges || [];
          
          // Create initial nodes with proper styling based on type
          const initialNodes: Node[] = apiNodes.map((node) => {
            const nodeId = node.id || '';
            const label = node.label || nodeId;
            const isBase = !node.is_head;
            
            return {
              id: nodeId,
              type: 'default',
              position: { x: 0, y: 0 }, // Will be calculated by dagre
              data: {
                label: (
                  <div className="node-label">
                    <div className="node-title">{label}</div>
                    {node.type && (
                      <div className="node-type">
                        {node.type === 'base' ? 'BASE' : 'FINE_TUNED'}
                      </div>
                    )}
                  </div>
                ),
              },
              style: {
                background: isBase ? '#E3F0FB' : '#E3F0FB',
                border: isBase ? '2px solid #0099FA' : '2px solid #FF8300',
                borderRadius: '8px',
                padding: '10px',
                minWidth: '150px',
                fontSize: '12px',
                fontWeight: isBase ? '600' : '500',
              },
            };
          });

          // Transform API edges to ReactFlow edges (using from_id and to_id)
          const initialEdges: Edge[] = apiEdges.map((edge, index) => {
            const sourceId = edge.from_id || '';
            const targetId = edge.to_id || '';
            const edgeId = edge.id || `edge-${index}`;

            return {
              id: edgeId,
              source: sourceId,
              target: targetId,
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#0099FA', strokeWidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#0099FA',
              },
              label: edge.label || '',
              labelStyle: {
                fill: '#5B7893',
                fontWeight: 500,
                fontSize: '11px',
              },
              labelBgStyle: {
                fill: 'white',
                fillOpacity: 0.8,
              },
            };
          });

          // Use dagre to calculate hierarchical layout
          const dagreGraph = new dagre.graphlib.Graph();
          dagreGraph.setDefaultEdgeLabel(() => ({}));
          dagreGraph.setGraph({ 
            rankdir: 'TB', // Top to Bottom
            nodesep: 100, // Horizontal separation between nodes
            ranksep: 150, // Vertical separation between ranks
            marginx: 50,
            marginy: 50,
          });

          // Add nodes to dagre graph
          initialNodes.forEach((node) => {
            dagreGraph.setNode(node.id, {
              width: 180, // Approximate node width
              height: 80, // Approximate node height
            });
          });

          // Add edges to dagre graph
          initialEdges.forEach((edge) => {
            dagreGraph.setEdge(edge.source, edge.target);
          });

          // Calculate layout
          dagre.layout(dagreGraph);

          // Apply calculated positions to nodes
          const transformedNodes: Node[] = initialNodes.map((node) => {
            const nodeWithPosition = dagreGraph.node(node.id);
            return {
              ...node,
              position: {
                x: nodeWithPosition.x - 90, // Center the node (half of width)
                y: nodeWithPosition.y - 40, // Center the node (half of height)
              },
            };
          });

          setNodes(transformedNodes);
          setEdges(initialEdges);
        } else {
          setGraphError('Failed to load graph data');
          setNodes([]);
          setEdges([]);
        }
      } catch (e: any) {
        const errorMsg = capitalizeError(e?.response?.data?.error?.message || e?.message) || 'Failed to load graph';
        setGraphError(errorMsg);
        setNodes([]);
        setEdges([]);
      } finally {
        setGraphLoading(false);
      }
    };

    loadGraph();
  }, [selectedIntegrationId, setNodes, setEdges]);

  const handleIntegrationChange = (integrationId: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (integrationId) {
      newParams.set('integration_id', integrationId);
      setStoredSelectedIntegrationId('admin', user?.id, integrationId);
    } else {
      newParams.delete('integration_id');
      setStoredSelectedIntegrationId('admin', user?.id, null);
    }
    setSearchParams(newParams);
  };

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const selectedIntegration = useMemo(() => {
    if (!selectedIntegrationId) return null;
    return integrations.find((i) => (i?.id || i?._id) === selectedIntegrationId) || null;
  }, [integrations, selectedIntegrationId]);

  const handleAdminTabChange = (tab: AdminTabType) => {
    if (tab === 'fine-tuning-graph') {
      sessionStorage.setItem('adminScrollY', String(window.scrollY));
      navigate('/admin/fine-tuning-graph');
      return;
    }
    if (tab === 'users') {
      navigate('/admin');
      return;
    }
    navigate(`/admin?tab=${tab}`);
  };

  // Restore scroll position when opening from Admin so the page doesn't jump
  useLayoutEffect(() => {
    const saved = sessionStorage.getItem('adminScrollY');
    if (saved !== null) {
      const y = parseInt(saved, 10);
      if (!Number.isNaN(y)) window.scrollTo(0, y);
      sessionStorage.removeItem('adminScrollY');
    }
  }, []);

  return (
    <div className="admin-page admin-shell-page connector-shell-page">
      <AdminTabBar activeTab={resolvedTab} onTabChange={handleAdminTabChange} />

      <div className="admin-shell-body">
        <ConnectorSectionShell
          eyebrow="Lineage"
          title="Fine tuning graph"
          subtitle="Visualize the lineage and relationships between fine-tuned models."
          icon="fa-project-diagram"
        >
          <div className="connector-section-panel">
            <div className="fine-tuning-graph-controls">
              <div className="integration-selector">
                <label htmlFor="integration-select">Integration</label>
                <select
                  id="integration-select"
                  value={selectedIntegrationId}
                  onChange={(e) => handleIntegrationChange(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select an integration</option>
                  {integrations.map((integration) => {
                    const id = integration?.id || integration?._id || '';
                    const url = integration?.url || integration?.endpoint || id;
                    const createdBy = integration?.created_by_name || integration?.created_by_email || '';
                    const label = createdBy ? `${url} (${createdBy})` : url;
                    return (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              {selectedIntegration && (
                <div className="integration-info">
                  <span className="info-label">Selected:</span>
                  <span className="info-value">
                    {selectedIntegration?.name || selectedIntegration?.url || selectedIntegrationId}
                    {(selectedIntegration?.created_by_name || selectedIntegration?.created_by_email) && (
                      <span className="integration-info__creator">
                        (created by{' '}
                        {selectedIntegration?.created_by_name || selectedIntegration?.created_by_email})
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="graph-container">
              {graphLoading && (
                <div className="graph-loading">
                  <div className="spinner"></div>
                  <p>Loading graph data...</p>
                </div>
              )}

              {graphError && (
                <div className="graph-error">
                  <p>{graphError}</p>
                </div>
              )}

              {!graphLoading && !graphError && nodes.length === 0 && selectedIntegrationId && (
                <div className="graph-empty">
                  <p>No graph data available for this integration</p>
                </div>
              )}

              {!graphLoading && !graphError && nodes.length > 0 && (
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  fitView
                  attributionPosition="bottom-left"
                >
                  <Background color="#E3F0FB" gap={16} />
                  <Controls />
                  <MiniMap
                    nodeColor={() => '#FF8300'}
                    maskColor="rgba(10, 42, 71, 0.1)"
                  />
                </ReactFlow>
              )}

              {!selectedIntegrationId && (
                <div className="graph-placeholder">
                  <p>Please select an integration to view the fine-tuning graph</p>
                </div>
              )}
            </div>
          </div>
        </ConnectorSectionShell>
      </div>
    </div>
  );
};

export default FineTuningGraphPage;
